import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Spinner from '../../../components/ui/Spinner'
import api from '../../../lib/api'
import { formatCurrency } from '../../../lib/formatters'
import { exportToExcel } from '../../../lib/excelExport'
import type { MonthlySalesResponse, MonthlySalesRow, Doctor, PaginatedResponse } from '../../../types'
import toast from 'react-hot-toast'

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

function getYearOptions() {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }))
}

function getMonthName(month: string) {
  return MONTHS.find((m) => m.value === month)?.label ?? month
}

export default function MonthlySalesReport() {
  const now = new Date()
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'))
  const [year, setYear] = useState(String(now.getFullYear()))
  const [doctorId, setDoctorId] = useState('')
  const [shouldFetch, setShouldFetch] = useState(false)

  // Fetch doctors for filter dropdown
  const { data: doctors } = useQuery({
    queryKey: ['doctors', 'report-filter'],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Doctor>>('/doctors?per_page=200')
      return data.data || []
    },
    staleTime: 60000,
  })

  const doctorOptions = [
    { value: '', label: 'All Doctors' },
    ...(doctors?.map((d) => ({ value: d.id, label: `${d.name} — ${d.clinicName}` })) ?? []),
  ]

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['reports', 'monthly-sales', month, year, doctorId],
    queryFn: async () => {
      const params: { month: string; year: string; doctor_id?: string } = { month, year }
      if (doctorId) params.doctor_id = doctorId
      const { data } = await api.get<{ data: MonthlySalesResponse }>('/reports/monthly-sales', { params })
      return data?.data ?? data
    },
    enabled: shouldFetch,
  })

  const rows: MonthlySalesRow[] = (reportData as MonthlySalesResponse)?.data ?? []
  const totals = (reportData as MonthlySalesResponse)?.totals ?? { total_sales: 0, invoice_count: 0 }

  const handleGenerate = () => {
    setShouldFetch(true)
  }

  const handleExportExcel = () => {
    if (rows.length === 0) {
      toast.error('No data to export')
      return
    }

    try {
      exportToExcel({
        sheetName: 'Monthly Sales Report',
        title: 'Monthly Sales Report',
        subtitle: `${getMonthName(month)} ${year}`,
        fileName: `Monthly_Sales_Report_${getMonthName(month)}_${year}.xls`,
        columns: [
          { header: 'Doctor Name', key: 'doctor_name', format: 'text', width: 250 },
          { header: 'Total Sales Amount', key: 'total_sales', format: 'currency', width: 150 },
        ],
        rows: rows.map((row) => ({
          doctor_name: row.doctor_name,
          total_sales: row.total_sales,
        })),
        totalsRow: {
          doctor_name: 'Grand Total',
          total_sales: totals.total_sales,
        },
        freezeHeaderRow: true,
      })
      toast.success('Report exported successfully')
    } catch {
      toast.error('Failed to export report')
    }
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <SearchableSelect
            label="Month *"
            value={month}
            onChange={setMonth}
            searchable={false}
            icon="fa-calendar"
            options={MONTHS}
          />
          <SearchableSelect
            label="Year *"
            value={year}
            onChange={setYear}
            searchable={false}
            icon="fa-calendar-alt"
            options={getYearOptions()}
          />
          <SearchableSelect
            label="Doctor"
            value={doctorId}
            onChange={setDoctorId}
            placeholder="All Doctors"
            options={doctorOptions}
          />
          <Button onClick={handleGenerate} loading={isFetching}>
            <i className="fas fa-play mr-2"></i>
            Generate
          </Button>
        </div>
      </Card>

      {/* Loading */}
      {isLoading && shouldFetch && (
        <div className="flex items-center justify-center h-48">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty state - before generate */}
      {!shouldFetch && (
        <Card>
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <i className="fas fa-chart-bar text-4xl mb-4 opacity-50"></i>
            <p className="text-lg font-medium">Monthly Sales Report</p>
            <p className="text-sm mt-1">Select Month & Year and click Generate to view the report</p>
          </div>
        </Card>
      )}

      {/* Empty state - no data */}
      {shouldFetch && !isLoading && rows.length === 0 && (
        <Card>
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <i className="fas fa-inbox text-4xl mb-4 opacity-50"></i>
            <p>No data available for selected month</p>
          </div>
        </Card>
      )}

      {/* Results */}
      {rows.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <p className="text-green-100 text-sm">Total Sales Amount</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totals.total_sales)}</p>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <p className="text-purple-100 text-sm">Doctors</p>
              <p className="text-2xl font-bold mt-1">{rows.length}</p>
            </Card>
          </div>

          {/* Export button */}
          <div className="flex justify-end">
            <Button variant="secondary" onClick={handleExportExcel}>
              <i className="fas fa-file-excel mr-2"></i>
              Export to Excel
            </Button>
          </div>

          {/* Table */}
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Doctor Name</th>
                    <th className="text-right">Total Sales Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.doctor_id}>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{row.doctor_name}</p>
                          {row.clinic_name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{row.clinic_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-right font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(row.total_sales)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 font-bold">
                    <td className="text-gray-900 dark:text-white">Grand Total</td>
                    <td className="text-right text-gray-900 dark:text-white">
                      {formatCurrency(totals.total_sales)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
